import React from 'react';
import { Box, Typography } from '@mui/material';
import type {
  ObjectHierarchicalMetadataValue,
  ObjectMetadataValue,
} from '../../types/ObjectHierarchicalMetadataValue';

interface GettyHierarchyBreadcrumbProps {
  value: ObjectMetadataValue | ObjectMetadataValue[] | null | undefined;
}

const isHierarchical = (
  v: ObjectMetadataValue,
): v is ObjectHierarchicalMetadataValue => !v.isFreetext;

const Row: React.FC<{ value: ObjectHierarchicalMetadataValue }> = ({ value }) => {
  const path = (value.breadcrumbs ?? []).join(' › ');
  return (
    <Box
      data-testid="getty-hierarchy-breadcrumb"
      sx={{
        mt: 1,
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 0.75,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Hierarchy: {path || value.textValue}
      </Typography>
    </Box>
  );
};

/**
 * Small breadcrumb shown beneath each Getty Vocab picker. For a selected
 * hierarchical term it lists the path through the AAT tree.
 * Renders nothing for freetext or empty values.
 * Multi-value pickers render one row per selected term.
 */
export const GettyHierarchyBreadcrumb: React.FC<GettyHierarchyBreadcrumbProps> = ({
  value,
}) => {
  if (!value) return null;
  const items = Array.isArray(value) ? value : [value];
  const hierarchical = items.filter(isHierarchical);
  if (hierarchical.length === 0) return null;
  return (
    <Box>
      {hierarchical.map((item) => (
        <Row key={item.valueUri} value={item} />
      ))}
    </Box>
  );
};
